import { type ReactNode } from 'react'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { twJoin } from 'tailwind-merge'
import { featureFlagsClient } from 'shared/feature-flags/client'
import { featureFlagKeys } from 'shared/feature-flags/feature-keys'
import { useToggle } from 'shared/hooks/use-toggle'
import useUser, { isDomainOwner, isWsAdmin, isWsAssistant } from 'shared/hooks/use-user'
import ChevronUpIcon from 'shared/icons/chevron-up-icon'
import HomeIcon from 'shared/icons/home-icon'
import MembersIcon from 'shared/icons/members-icon'
import ModerationBellIcon from 'shared/icons/moderation-bell-icon'
import ModerationHistoryIcon from 'shared/icons/moderation-history-icon'
import PlusIcon from 'shared/icons/plus-icon'
import PostsIcon from 'shared/icons/posts-icon'
import { ScheduledPostsIcon } from 'shared/icons/scheduled-posts-icon'
import SettingsIcon from 'shared/icons/settings-icon'
import AddTopic from 'modules/community/components/add-topic'
import { CommunitySearchForm } from 'modules/community/components/search-form'
import { CommunitySearchFormTrigger } from 'modules/community/components/search-form/trigger'
import { TopicList } from 'modules/community/components/topic/components/topic-list'
import { useCommunityWithTopics } from 'modules/community/hooks/use-community-with-topics'
import { hasPermission } from 'modules/community/utils/abac-roles-permissions'

const homeOptionClassName =
  'block w-full cursor-pointer overflow-hidden text-ellipsis px-5 py-1.5 text-left text-text-primary/60 hover:text-brand-primary'

function MobileNav(): ReactNode {
  const { t } = useTranslation()
  const { user } = useUser()
  const { data: community } = useCommunityWithTopics()
  const router = useRouter()
  const { topicPath, communityPath, q } = router.query

  const canViewScheduledPosts = hasPermission(user, 'scheduledPosts', 'schedule', undefined)
  const isReportsEnabled = featureFlagsClient.isOn(featureFlagKeys.enableCommunityReports)
  const canViewModeration = isReportsEnabled && hasPermission(user, 'moderation', 'view', null)
  const [isSearchOpen, toggleSearch] = useToggle(!!q)

  if (!community || !user) {
    return (
      <div className="flex animate-pulse justify-between px-5 py-2">
        <div className="h-6 w-10/12 rounded-lg bg-skeleton-primary" />
        <div className="h-6 w-1/12 rounded-lg bg-skeleton-primary" />
      </div>
    )
  }

  const isHomePage = router.asPath === `/community/${communityPath}`
  const selectedTopic = community.topics.find(topic => topic.path === topicPath)
  const isSettingsPage = router.pathname.includes('settings')
  const isMembersPage = router.pathname.includes('members')
  const isPendingPostsPage = router.pathname.includes('pending-posts')
  const isScheduledPostsPage = router.pathname.includes('scheduled-posts')
  const isModerationAlertsPage = router.pathname.includes('moderation/alerts')
  const isModerationHistoryPage = router.pathname.includes('moderation/history')

  const menuItems = [
    {
      href: `/community/${communityPath}`,
      icon: <HomeIcon />,
      isActive: isHomePage || selectedTopic,
      show: true,
    },
    {
      href: `/community/${communityPath}/members`,
      icon: <MembersIcon />,
      isActive: isMembersPage,
      show: true,
    },
    {
      href: `/community/${communityPath}/moderation/alerts`,
      icon: <ModerationBellIcon />,
      isActive: isModerationAlertsPage,
      show: canViewModeration,
    },
    {
      href: `/community/${communityPath}/moderation/history`,
      icon: <ModerationHistoryIcon />,
      isActive: isModerationHistoryPage,
      show: canViewModeration,
    },
    {
      href: `/community/${communityPath}/settings`,
      icon: <SettingsIcon />,
      isActive: isSettingsPage,
      show: isDomainOwner(user) || isWsAdmin(user),
    },
    {
      href: `/community/${communityPath}/pending-posts`,
      icon: <PostsIcon />,
      isActive: isPendingPostsPage,
      show: (isDomainOwner(user) || isWsAdmin(user)) && !community.autoPostsApproval,
    },
    {
      href: `/community/${communityPath}/scheduled-posts`,
      icon: <ScheduledPostsIcon />,
      isActive: isScheduledPostsPage,
      show: canViewScheduledPosts,
    },
  ].filter(item => item.show)

  const topicsPanelKey = selectedTopic?.path ?? 'home'

  return (
    <div className="sticky top-0 z-20 flex w-full flex-col lg:hidden">
      <div className="flex w-full">
        {menuItems.map(({ href, icon, isActive }) => (
          <Link
            href={href}
            key={href}
            className={twJoin(
              `flex flex-1 justify-center border-b-[3px] bg-background-secondary py-4`,
              isActive
                ? 'border-brand-primary text-brand-primary'
                : `border-text-secondary/30 text-text-secondary`,
            )}
          >
            {icon}
          </Link>
        ))}
      </div>
      <div className="relative flex flex-row items-center gap-x-4 border-b border-b-text-secondary/30 bg-background-secondary px-5 py-3">
        <Disclosure key={topicsPanelKey} as="div" className="min-w-0 flex-1">
          {({ close }) => (
            <>
              <div className="flex min-w-0 flex-1 items-center py-3">
                <DisclosureButton
                  data-testid="mobile-topics-panel-trigger"
                  className="group/topics-panel relative flex w-full cursor-default items-center gap-x-1.5 rounded-lg text-left font-semibold text-text-secondary focus:outline-hidden focus-visible:ring-2 focus-visible:ring-background-secondary/75 focus-visible:ring-offset-2 sm:text-sm"
                >
                  <span className="line-clamp-2 block overflow-hidden text-ellipsis">
                    {selectedTopic ? selectedTopic.name : t(`mobile_nav.home`)}
                  </span>
                  <ChevronUpIcon className="size-6 shrink-0 transition-transform duration-200 group-data-open/topics-panel:rotate-180" />
                </DisclosureButton>
              </div>

              <DisclosurePanel
                transition
                className="absolute inset-x-0 top-full z-10 origin-top transition duration-100 ease-in data-closed:opacity-0"
              >
                <div className="max-h-60 overflow-auto bg-background-secondary py-3 text-base shadow-lg focus:outline-hidden">
                  <button
                    type="button"
                    className={homeOptionClassName}
                    onClick={() => {
                      close()
                      void router.push(`/community/${communityPath}`)
                    }}
                  >
                    {t(`mobile_nav.home`)}
                  </button>

                  <TopicList topicItemClassName="px-5 py-1.5" />
                </div>
              </DisclosurePanel>
            </>
          )}
        </Disclosure>
        {(isDomainOwner(user) || isWsAdmin(user) || isWsAssistant(user)) && (
          <div className={'flex items-center gap-2 text-text-secondary'}>
            <AddTopic className="text-text-secondary" icon={PlusIcon} />
          </div>
        )}
        <CommunitySearchFormTrigger className="justify-self-end" onOpen={toggleSearch} />
      </div>
      {isSearchOpen && (
        <CommunitySearchForm className="rounded-none border-b border-b-text-secondary/30" />
      )}
    </div>
  )
}

export default MobileNav
